import { forwardRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useAppDispatch } from '@/Application/Hooks/Redux';
import { setIsKeyboardOpen } from '@/modules/perseo-billing/store/slices/billing';
import type { ITypographyProps } from '@/modules/perseo-billing/ui/react/src/components/beta';
import { Typography } from '@/modules/perseo-billing/ui/react/src/components/beta';

import type { TInputProps } from './input.interface';

type TFeedBackProps = ITypographyProps & {
  type?: string;
};

export const FeedbackMessage = ({
  type,
  size = 'body2',
  leading = '16px',
  color = 'gray',
  weight = 'medium',
  children
}: TFeedBackProps) => {
  if (type === 'error') {
    color = 'error';
  }

  return (
    <div className="co-w-full co-ml-4px co-mt-4px">
      {typeof children === 'string' ? (
        <Typography size={size} color={color} leading={leading} weight={weight}>
          {children}
        </Typography>
      ) : (
        children
      )}
    </div>
  );
};

export const CardInput = forwardRef<HTMLInputElement, TInputProps>(
  (
    {
      label,
      name,
      error,
      disabled = false,
      type = 'text',
      endIcon,
      size = 'medium',
      example = '',
      labelSize = 'body2',
      labelWeight = 'semibold',
      placeHolder = '',
      isValid,
      onFocus,
      onBlur,
      onEndIconClick,
      tabIndex = 0,
      required,
      validate,
      ...props
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const [isFocused, setIsFocused] = useState(false);
    const { register } = useFormContext();

    let inputClass = '';

    if (error) {
      inputClass =
        'co-border-pds-error-500 focus:co-border-pds-error-500 focus:co-ring-pds-error-500';
    } else {
      if (disabled) {
        inputClass = 'disabled co-bg-gray-xlight co-border-disabled-main';
      } else if (isValid) {
        inputClass =
          'co-border-dark-green focus:co-border-dark-green focus:co-ring-dark-green';
      } else {
        inputClass =
          'co-border-pds-neutral-300 focus:co-border-pds-primary-500 focus:co-border-1.5';
      }
    }

    return (
      <div className="co-flex co-input-item co-flex-wrap co-relative">
        <label
          className={`
                        co-w-full
                        co-flex
                        co-items-center
                        co-relative
                        co-m-0
                        co-pl-0
                        co-label-${size}
                    `}
          htmlFor={name}
        >
          <input
            disabled={disabled}
            className={`
                            ${inputClass}
                            co-input-text-${size}
                            co-px-16px
                            co-w-full
                            co-text-[14px]
                            co-h-[46px]
                            co-border-1
                            co-rounded-md
                            co-font-semibold
                            co-form-input
                            focus:outline-none
                            focus:co-ring-1
                            co-transition-all co-duration-300
                        `}
            type={type}
            id={name}
            placeholder={placeHolder ?? ''}
            tabIndex={tabIndex}
            required={required}
            onFocus={(e) => {
              setIsFocused(true);
              if (onFocus) {
                onFocus(e);
              }
            }}
            {...register(name, {
              /* eslint-disable-next-line */
              // @ts-ignore-next-line
              validate: validate || {}
            })}
            onBlur={(e) => {
              if (onBlur) {
                onBlur(e);
              }
              dispatch(setIsKeyboardOpen(false));
            }}
            {...props}
            ref={ref}
          />
          <span
            className={`
                            co-text-common-default
                            co-px-8px
                            co-rounded-md
                            co-top-auto
                            ${
                              disabled
                                ? 'co-bg-transparent'
                                : 'co-bg-common-white'
                            }
                            co-absolute
                            co-left-8px
                        `}
          >
            <Typography
              size={labelSize}
              weight={labelWeight}
              leading="16px"
              noWrap
            >
              {label}
            </Typography>
          </span>
          {endIcon && (
            <div
              className={`
                                    co-w-24px co-h-auto
                                    co-flex
                                    co-justify-center
                                    co-items-center
                                    co-absolute
                                    co-right-0
                                    co-top-0
                                    co-bottom-0
                                    co-mr-[15px]
                                    co-my-[1px]
                            `}
              onClick={onEndIconClick}
              tabIndex={-1}
            >
              {endIcon}
            </div>
          )}
        </label>
        {isFocused && example && !error && !isValid && (
          <FeedbackMessage>{example}</FeedbackMessage>
        )}
        {error && <FeedbackMessage type="error">{error}</FeedbackMessage>}
      </div>
    );
  }
);
